
import React, { useState } from 'react';
import {
    Container,
    Paper,
    Typography,
    Box,
    Grid,
    Button,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Alert
} from '@mui/material';
import {
    PictureAsPdf as PdfIcon,
    TableView as ExcelIcon,
    Search as SearchIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { reportsApi } from '../services/api';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

const SalesReportsPage = () => {
    const { t } = useTranslation();
    const [startDate, setStartDate] = useState(new Date());
    const [endDate, setEndDate] = useState(new Date());
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState(null);
    const [error, setError] = useState('');

    // Quick filters
    const applyFilter = (type) => {
        const end = new Date();
        const start = new Date();

        switch (type) {
            case 'today':
                start.setHours(0, 0, 0, 0);
                break;
            case 'week':
                start.setDate(end.getDate() - 7);
                break;
            case 'month':
                start.setMonth(end.getMonth() - 1);
                break;
            case 'biweekly':
                start.setDate(end.getDate() - 14);
                break;
            default:
                break;
        }
        setStartDate(start);
        setEndDate(end);
    };

    const generateReport = async () => {
        if (!startDate || !endDate) return;

        setLoading(true);
        setError('');

        try {
            const response = await reportsApi.getSalesReport(
                format(startDate, 'yyyy-MM-dd'),
                format(endDate, 'yyyy-MM-dd')
            );
            setReportData(response.data.report);
        } catch (err) {
            console.error('Report generation failed:', err);
            setError(t('reports.failedToLoad'));
        } finally {
            setLoading(false);
        }
    };

    const exportPDF = () => {
        if (!reportData) return;

        const doc = new jsPDF();

        doc.setFontSize(20);
        doc.setTextColor(40);
        doc.text(t('reports.salesReports'), 14, 22);

        doc.setFontSize(11);
        doc.setTextColor(100);
        doc.text(`${t('reports.period')}: ${format(new Date(reportData.period.start), 'MMM dd, yyyy')} - ${format(new Date(reportData.period.end), 'MMM dd, yyyy')}`, 14, 32);

        autoTable(doc, {
            startY: 40,
            head: [[t('reports.totalSales'), t('reports.orders'), t('reports.avgOrderValue')]],
            body: [[
                `${reportData.totalSales}`,
                reportData.orderCount,
                `${reportData.avgOrderValue}`
            ]],
            theme: 'plain',
        });

        doc.text(t('reports.dailyBreakdown'), 14, doc.lastAutoTable.finalY + 15);

        autoTable(doc, {
            startY: doc.lastAutoTable.finalY + 20,
            head: [[t('reports.date'), t('reports.orders'), t('reports.revenue')]],
            body: reportData.dailyBreakdown.map(d => [
                d.date,
                d.count,
                `${d.total.toFixed(2)}`
            ]),
            theme: 'striped',
            headStyles: { fillColor: [66, 66, 66] }
        });

        doc.save(`sales-report-${format(new Date(), 'yyyyMMdd')}.pdf`);
    };

    const exportExcel = () => {
        if (!reportData) return;

        const wb = XLSX.utils.book_new();

        const summaryData = [
            [t('reports.salesReportSummary')],
            [t('reports.generatedAt'), new Date().toISOString()],
            [t('reports.periodStart'), reportData.period.start],
            [t('reports.periodEnd'), reportData.period.end],
            [],
            [t('reports.totalSales'), reportData.totalSales],
            [t('reports.totalOrders'), reportData.orderCount],
            [t('reports.avgOrderValue'), reportData.avgOrderValue]
        ];
        const wsSummary = XLSX.utils.aoa_to_sheet(summaryData);
        XLSX.utils.book_append_sheet(wb, wsSummary, t('reports.summary'));

        const wsDaily = XLSX.utils.json_to_sheet(reportData.dailyBreakdown);
        XLSX.utils.book_append_sheet(wb, wsDaily, t('reports.dailyBreakdown'));

        const wsOrders = XLSX.utils.json_to_sheet(reportData.orders);
        XLSX.utils.book_append_sheet(wb, wsOrders, t('reports.allOrders'));

        XLSX.writeFile(wb, `sales-report-${format(new Date(), 'yyyyMMdd')}.xlsx`);
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Container maxWidth="xl" sx={{ mt: 4, mb: 6 }}>
                <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Typography variant="h4" fontWeight="bold" gutterBottom>
                            {t('reports.salesReports')}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            {t('reports.salesReportsSubtitle')}
                        </Typography>
                    </Box>
                </Box>

                {/* Filter Section */}
                <Paper sx={{ p: 3, mb: 4, borderRadius: 3 }}>
                    <Grid container spacing={3} alignItems="center">
                        <Grid>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button variant="outlined" size="small" onClick={() => applyFilter('today')}>{t('reports.today')}</Button>
                                <Button variant="outlined" size="small" onClick={() => applyFilter('week')}>{t('reports.last7Days')}</Button>
                                <Button variant="outlined" size="small" onClick={() => applyFilter('biweekly')}>{t('reports.biWeekly')}</Button>
                                <Button variant="outlined" size="small" onClick={() => applyFilter('month')}>{t('reports.thisMonth')}</Button>
                            </Box>
                        </Grid>
                        <Grid>
                            <DatePicker
                                label={t('reports.startDate')}
                                value={startDate}
                                onChange={(newValue) => setStartDate(newValue)}
                                slotProps={{ textField: { size: 'small' } }}
                            />
                        </Grid>
                        <Grid>
                            <DatePicker
                                label={t('reports.endDate')}
                                value={endDate}
                                onChange={(newValue) => setEndDate(newValue)}
                                slotProps={{ textField: { size: 'small' } }}
                            />
                        </Grid>
                        <Grid>
                            <Button
                                variant="contained"
                                startIcon={<SearchIcon />}
                                onClick={generateReport}
                                disabled={loading}
                            >
                                {loading ? t('reports.generating') : t('reports.generateReport')}
                            </Button>
                        </Grid>
                    </Grid>
                </Paper>

                {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
                )}

                {/* Results Section */}
                {reportData && (
                    <Box>
                        {/* Quick Stats */}
                        <Grid container spacing={3} sx={{ mb: 4 }}>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <Card sx={{ bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                                    <CardContent>
                                        <Typography variant="h6" component="div">{t('reports.totalSales')}</Typography>
                                        <Typography variant="h3" fontWeight="bold">
                                            {reportData.totalSales.toLocaleString()} {t('common.currency')}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <Card sx={{ bgcolor: 'secondary.light', color: 'secondary.contrastText' }}>
                                    <CardContent>
                                        <Typography variant="h6" component="div">{t('reports.totalOrders')}</Typography>
                                        <Typography variant="h3" fontWeight="bold">
                                            {reportData.orderCount}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <Card sx={{ bgcolor: 'success.light', color: 'success.contrastText' }}>
                                    <CardContent>
                                        <Typography variant="h6" component="div">{t('reports.avgOrderValue')}</Typography>
                                        <Typography variant="h3" fontWeight="bold">
                                            {reportData.avgOrderValue.toFixed(2)} {t('common.currency')}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>

                        {/* Daily Breakdown Table */}
                        <Card sx={{ mb: 4, borderRadius: 2 }}>
                            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: 1, borderColor: 'divider' }}>
                                <Typography variant="h6" component="div">{t('reports.dailyBreakdown')}</Typography>
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        startIcon={<PdfIcon />}
                                        onClick={exportPDF}
                                    >
                                        {t('reports.exportPDF')}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="success"
                                        startIcon={<ExcelIcon />}
                                        onClick={exportExcel}
                                    >
                                        {t('reports.exportExcel')}
                                    </Button>
                                </Box>
                            </Box>
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>{t('reports.date')}</TableCell>
                                            <TableCell align="right">{t('reports.orders')}</TableCell>
                                            <TableCell align="right">{t('reports.revenue')}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {reportData.dailyBreakdown.map((day) => (
                                            <TableRow key={day.date} hover>
                                                <TableCell>{day.date}</TableCell>
                                                <TableCell align="right">{day.count}</TableCell>
                                                <TableCell align="right">{day.total.toFixed(2)} {t('common.currency')}</TableCell>
                                            </TableRow>
                                        ))}
                                        {reportData.dailyBreakdown.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={3} align="center">{t('reports.noDataForPeriod')}</TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Card>

                    </Box>
                )}
            </Container>
        </LocalizationProvider>
    );
};

export default SalesReportsPage;
